import { Flex } from 'app/components/common/Flex';
import TimePeriodUtils from 'app/common/util/TimePeriodUtils';
import React, { PureComponent } from 'react';
import { TimeSlice } from './RegulationInteractionGraph';
import { MeasureId, RegulationOrMcdmOnlyDto, RegulationState } from 'app/model/CORE';
import { DisplayData, RegulationInteractionUtils } from './RegulationInteractionUtils';
import MeasureUtils from 'app/common/util/MeasureUtils';
import { Col, Row } from 'antd';
import './RegulationInteractionSummary.less';

const TRANSPARENT = 'rgba(0,0,0,0)';
const BACKGROUND = '#3A3A3A';
const BLACK = '#121212';

function getNbFlightsPerTimeSlice(timeSlices: Array<TimeSlice>, regulId: string) {
    if (!regulId || !timeSlices) return undefined;

    return timeSlices.map(
        timeSlice => timeSlice.subTotals.find(subTotal => subTotal.regulationId === regulId)?.nbFlights || 0,
    );
}

function getNbFlights(flightsPerTimeSlice: Array<number>) {
    return flightsPerTimeSlice?.reduce((sum, current) => sum + current, 0);
}

function hashedBackground(color: string) {
    return `repeating-linear-gradient(-45deg, ${TRANSPARENT}, ${TRANSPARENT} .5em, ${color} .5em, ${color} 1em)`;
}

function getStartingStyle(
    studiedRegulation: RegulationOrMcdmOnlyDto,
    regulation: RegulationOrMcdmOnlyDto,
    finished: boolean,
    color: string,
) {
    return TimePeriodUtils.containsInc(regulation.applicability, studiedRegulation.applicability.from)
        ? finished
            ? {
                  background: `linear-gradient(90deg, ${BACKGROUND} 0%, ${TRANSPARENT} 200%), ${hashedBackground(color)}`,
              }
            : {
                  background: `linear-gradient(90deg, ${TRANSPARENT} 0%, ${color} 100%)`,
              }
        : {
              backgroundColor: TRANSPARENT,
          };
}

function getEndingStyle(
    studiedRegulation: RegulationOrMcdmOnlyDto,
    regulation: RegulationOrMcdmOnlyDto,
    finished: boolean,
    color: string,
) {
    return TimePeriodUtils.contains(regulation.applicability, studiedRegulation.applicability.to)
        ? finished
            ? {
                  background: `linear-gradient(90deg, ${TRANSPARENT} -100%, ${BACKGROUND} 100%), ${hashedBackground(color)}`,
              }
            : {
                  background: `linear-gradient(90deg, ${color} 0%, ${TRANSPARENT} 100% )`,
              }
        : {
              backgroundColor: TRANSPARENT,
          };
}

function getIntersectionStyle(
    studiedRegulation: RegulationOrMcdmOnlyDto,
    regulation: RegulationOrMcdmOnlyDto,
    finished: boolean,
    color: string,
) {
    const factor = 100 / TimePeriodUtils.getDurationInMs(studiedRegulation.applicability);

    const intersectionPeriod = TimePeriodUtils.intersection(regulation.applicability, studiedRegulation.applicability);
    const intersection = {
        style: finished ? { background: hashedBackground(color) } : { backgroundColor: color },
        size: factor * TimePeriodUtils.getDurationInMs(intersectionPeriod),
        from: intersectionPeriod.from,
    };

    const intersectionEdges = TimePeriodUtils.subtract(studiedRegulation.applicability, regulation.applicability).map(
        timePeriod => ({
            style: { backgroundColor: BLACK },
            size: factor * TimePeriodUtils.getDurationInMs(timePeriod),
            from: timePeriod.from,
        }),
    );

    return [intersection, ...intersectionEdges].sort((a, b) => a.from.getTime() - b.from.getTime());
}

interface RegulationInteractionSummaryProps {
    measureId: MeasureId;
    displayData: DisplayData;
    regulations: Array<RegulationOrMcdmOnlyDto>;
}

export class RegulationInteractionSummary extends PureComponent<RegulationInteractionSummaryProps> {
    render() {
        const studiedRegulation = RegulationInteractionUtils.getRegulation(
            this.props.regulations,
            this.props.measureId.id,
        );
        return (
            <Flex className="regulation-interaction-summary overflow" direction="vertical">
                {this.props.displayData &&
                    Array.from(this.props.displayData?.colors.keys())
                        .filter(regulId => regulId !== this.props.measureId?.id)
                        .sort((regA, RegB) => {
                            return (
                                getNbFlights(getNbFlightsPerTimeSlice(this.props.displayData?.timeSlices, regA)) -
                                getNbFlights(getNbFlightsPerTimeSlice(this.props.displayData?.timeSlices, RegB))
                            );
                        })
                        .reverse()
                        .map((regulId, regulIdx) => {
                            const regulation = RegulationInteractionUtils.getRegulation(
                                this.props.regulations,
                                regulId,
                            );
                            if (!regulation) return undefined;

                            const flightsPerTimeSlice = getNbFlightsPerTimeSlice(
                                this.props.displayData?.timeSlices,
                                regulId,
                            );
                            const regulNbFlights = getNbFlights(flightsPerTimeSlice);
                            if (regulNbFlights === 0) return undefined;

                            const color = this.props.displayData?.colors.get(regulId);
                            const finished = [
                                RegulationState.TERMINATED,
                                RegulationState.CANCELLED,
                                RegulationState.CANCELLING,
                            ].includes(MeasureUtils.getRegulationState(regulation));

                            const startingStyle = getStartingStyle(studiedRegulation, regulation, finished, color);
                            const intersectionStyle = getIntersectionStyle(
                                studiedRegulation,
                                regulation,
                                finished,
                                color,
                            );
                            const endingStyle = getEndingStyle(studiedRegulation, regulation, finished, color);

                            return (
                                <Row key={`"summary-${regulIdx}"`} gutter={[8, 8]} className="regulation-row">
                                    <Col span={4} className="regulation-info">
                                        <div>{regulId}</div>
                                        <div className="regulation-flights">
                                            {regulNbFlights} flight{regulNbFlights === 1 ? '' : 's'} delayed
                                        </div>
                                    </Col>
                                    <Col span={20} className="regulation-applicability">
                                        <Flex direction="vertical">
                                            <Flex className="colored-periods">
                                                <Flex size={'4em'}>
                                                    <span className="time-period" style={startingStyle} />
                                                </Flex>
                                                <Flex className="interaction-container">
                                                    {this.props.displayData?.timeSlices.map((_timeSlice, sliceIdx) => (
                                                        <Flex
                                                            className="interaction-container borders"
                                                            key={`timeslice-${sliceIdx}`}>
                                                            <span className="time-period bordered" />
                                                        </Flex>
                                                    ))}
                                                    <Flex className="time-period colored">
                                                        {intersectionStyle.map(({ size, style }, idx) => (
                                                            <Flex key={`intersection-${idx}`} size={`${size}%`}>
                                                                <span className="time-period" style={style} />
                                                            </Flex>
                                                        ))}
                                                    </Flex>
                                                </Flex>
                                                <Flex size={'4em'}>
                                                    <span className="time-period bordered" style={endingStyle} />
                                                </Flex>
                                            </Flex>
                                            <Flex>
                                                <Flex size={'4em'} />
                                                {flightsPerTimeSlice.map((nbFlights, sliceIdx) => (
                                                    <Flex
                                                        className="time-period-text bordered"
                                                        grow={true}
                                                        shrink={true}
                                                        size={'0%'}
                                                        key={`"timeslice-nbFlights-${sliceIdx}"`}>
                                                        {nbFlights ? nbFlights : '-'}
                                                    </Flex>
                                                ))}
                                                <Flex size={'4em'} className="bordered" />
                                            </Flex>
                                        </Flex>
                                    </Col>
                                </Row>
                            );
                        })}
            </Flex>
        );
    }
}
